// Oylan stand: the homework page (oylan/spec_domashka.md). The logic is in homework.js.
import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import homework from "../content-sources/oylan/homework.json";
import { current, homeworkLabel, loadJournal, newEntry, saveJournal, submit, submitStep } from "./homework";
import { todayKey } from "./session";
import { pushJournal } from "./journalSync";
import { Figure, Shapes } from "./Figure";

const STATUS = {
    new: "не начата", done: "сдана", steps: "решаем по шагам", review: "повторить", learned: "освоена",
};
// The page has its own light background: outside a lesson OATutor leaves the body dark in dark mode.
export const page = { padding: 16, minHeight: "100vh", background: "#fafafa", color: "#222" };
const box = { padding: 16, margin: "12px 0", textAlign: "left" };
const note = (color) => ({ ...box, background: color, borderRadius: 8, fontSize: "1.25rem", lineHeight: 1.5 });
// Big type on the task screen: the child reads it from a distance, small text blended together.
const bigInput = { fontSize: "1.6rem", padding: "14px 16px", width: 220 };
const bigButton = { fontSize: "1.3rem", padding: "12px 28px", textTransform: "none" };
const question = { fontSize: "1.8rem", lineHeight: 1.45, margin: "20px 0", padding: "20px 24px", background: "#f1f6fb", borderLeft: "6px solid #43729c", borderRadius: 8 };
const hint = { fontSize: "1.25rem", color: "#555", margin: "16px 0 12px" };

export function dayLabel(day) {
    const [, m, d] = day.split("-");
    return `${d}.${m}`;
}

function statusOf(task, entry, today) {
    const now = current(task, entry, today);
    if (now.mode === "review") return "повторить сегодня";
    if (now.mode === "wait") return `повтор ${dayLabel(now.due)}`;
    return STATUS[(entry || newEntry()).status];
}

function Answer({ item, onAnswer, label = "Проверить" }) {
    const [text, setText] = useState("");
    if (item.kind === "choice") {
        return (
            <div>
                {item.choices.map((c) => (
                    <Button key={c} variant="outlined" style={{ margin: 6, textTransform: "none", fontSize: "1.4rem", padding: "10px 24px" }} onClick={() => onAnswer(c)}>{c}</Button>
                ))}
            </div>
        );
    }
    const send = () => {
        if (!text.trim()) return;
        onAnswer(text);
        setText("");
    };
    return (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <TextField value={text} onChange={(e) => setText(e.target.value)} placeholder="Твой ответ" variant="outlined" autoFocus
                       onKeyDown={(e) => e.key === "Enter" && send()} inputProps={{ "data-testid": "homework-input", style: bigInput }} />
            <Button variant="contained" color="primary" style={bigButton} onClick={send}>{label}</Button>
        </div>
    );
}

function Task({ task, entry, today, onChange, onBack }) {
    const [message, setMessage] = useState(null);
    const now = current(task, entry, today);
    const item = now.item;

    const answer = (input) => {
        const r = submit(task, entry, input, today);
        onChange(r.entry);
        if (r.ok) {
            const later = current(task, r.entry, today);
            setMessage({ color: "#e6f4ea", text: later.mode === "wait" ? `Верно! Повторим ${dayLabel(later.due)}.` : "Верно!", method: item.method });
        } else if (r.entry.status === "steps") {
            setMessage({ color: "#fff4e5", text: `${r.diagnosis.text} Давай решим по шагам.` });
        } else {
            setMessage({ color: "#fff4e5", text: `${r.diagnosis.text} Попробуй ещё раз.` });
        }
    };

    const step = (input) => {
        const solved = item.steps[entry?.stepAt || 0];
        const r = submitStep(task, entry, input, today);
        onChange(r.entry);
        if (r.shown) setMessage({ color: "#e8f0fe", text: `Смотри: ${r.shown.explain.replace(/\.?$/, ".")} Ответ шага: ${r.shown.answer}. Запомни этот шаг.`, shapes: solved.shapes });
        else if (!r.ok) setMessage({ color: "#fff4e5", text: [r.note, "Не так. Попробуй ещё раз."].filter(Boolean).join(" ") });
        // A right step still shows why it is right: the child must see the reason, not only the number.
        else if (r.finished) setMessage({ color: "#e6f4ea", text: `Верно! ${solved.explain} Все шаги сделаны! Такую задачу с другими числами решим в другой день, сама.`, method: item.method, shapes: solved.shapes });
        else setMessage({ color: "#e6f4ea", text: `Верно! ${solved.explain}`, shapes: solved.shapes });
    };

    return (
        <Paper style={{ padding: "24px 32px", maxWidth: 820, margin: "0 auto" }}>
            <Button onClick={onBack} style={{ fontSize: "1.1rem" }}>← Все задачи</Button>
            <h2 style={{ margin: "16px 0 4px", fontSize: "1.6rem" }}>{task.id}. {(item || task).title}</h2>
            <div style={{ color: "#777", fontSize: "1.1rem" }}>{task.sheet}</div>
            {item && <div style={question}>{item.body} <b>{item.question}</b>{item.figure && <div style={{ marginTop: 16 }}><Figure figure={item.figure} /></div>}</div>}
            {now.mode === "own" && (
                <>
                    <p style={hint}>Сначала реши в тетради. Потом напиши сюда свой ответ.</p>
                    <Answer key={entry?.tries?.length || 0} item={item} onAnswer={answer} />
                </>
            )}
            {now.mode === "review" && (
                <>
                    <p style={hint}>Повторение. Реши сама, без подсказок.</p>
                    <Answer key={entry.tries.length} item={item} onAnswer={answer} />
                </>
            )}
            {now.mode === "steps" && (
                <>
                    {item.steps.slice(0, entry?.stepAt || 0).map((s, i) => (
                        <div key={i} style={{ color: "#555", fontSize: "1.2rem", margin: "6px 0" }}>✓ {s.q} <b>{s.answer}</b></div>
                    ))}
                    <p style={{ fontSize: "1.4rem", margin: "16px 0 12px" }}>Шаг {(entry?.stepAt || 0) + 1} из {item.steps.length}. {item.steps[entry?.stepAt || 0].q}</p>
                    <Answer key={`${entry?.stepAt}-${entry?.stepWrong}`} item={item.steps[entry?.stepAt || 0]} onAnswer={step} label="Ответить" />
                </>
            )}
            {message && (
                <div style={note(message.color)} data-testid="homework-message">
                    {message.text}
                    <Shapes figure={item?.figure} shapes={message.shapes} />
                    {message.method && <div style={{ marginTop: 8 }}>Способ: {message.method}</div>}
                </div>
            )}
            {now.mode === "wait" && !message && <p>Эту задачу повторим {dayLabel(now.due)}.</p>}
            {(now.mode === "done" || now.mode === "learned") && !message && (
                <div style={note("#e6f4ea")}>Задача {STATUS[now.mode]}. Способ: {task.method}</div>
            )}
        </Paper>
    );
}

export default function HomeworkPage() {
    const today = todayKey();
    const [journal, setJournal] = useState(loadJournal);
    const [open, setOpen] = useState(null);
    useEffect(() => {
        // The journal kept so far, and anything that did not reach the parent's site last time.
        const kept = loadJournal();
        if (Object.keys(kept).length) pushJournal(kept);
    }, []);
    const change = (id) => (entry) => {
        const next = { ...journal, [id]: entry };
        setJournal(next);
        saveJournal(next);
        pushJournal(next);
    };
    const tasks = homework.flatMap((l) => l.tasks);
    const task = tasks.find((t) => t.id === open);
    if (task) {
        return <div style={page}><Task key={task.id} task={task} entry={journal[task.id]} today={today}
                                                  onChange={change(task.id)} onBack={() => setOpen(null)} /></div>;
    }
    return (
        <div style={page}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <Button href="#/">← На главную</Button>
            {homework.map((lesson) => (
                <div key={lesson.lesson}>
                    <h2>{homeworkLabel(lesson)}</h2>
                    {lesson.tasks.map((t) => {
                        const status = statusOf(t, journal[t.id], today);
                        return (
                            <Paper key={t.id} style={{ ...box, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                                   onClick={() => setOpen(t.id)} data-testid={`homework-${t.id}`}>
                                <span><b>{t.id}</b> {t.title}</span>
                                <span style={{ color: status === "повторить сегодня" ? "#b26a00" : "#666", fontWeight: status === "повторить сегодня" ? 600 : 400 }}>{status}</span>
                            </Paper>
                        );
                    })}
                </div>
            ))}
          </div>
        </div>
    );
}
